import { devices_config } from '@devices/index';
import { expandLabelConfig } from 'src/configs/dash-device-labels/presets';
import {
	resolveDashInfoLabelsConfig,
	type DeviceConfigForDashLabels,
} from 'src/configs/dash-device-labels/resolve_config';
import { statusMatchesKey } from 'src/helpers/dash_device_info_labels';
import { mapIconSolidWeightToLight } from 'src/helpers/new-layout-helpers';
import type { Device } from 'src/types/Device';

export type DeviceTableStatusColumn = {
	id: string;
	keys: string[];
	label: string;
};

export type DeviceTableStatusCell = {
	value?: string;
	icon?: string;
};

/**
 * Canonical column order for dash-device-label preset keys.
 * Alias groups (e.g. temperature / curr_temperature) share one column so mixed
 * device lists don't grow duplicate headers. Unknown preset keys append after.
 */
export const DEVICE_TABLE_STATUS_COLUMNS: DeviceTableStatusColumn[] = [
	{ id: 'power', keys: ['power'], label: 'Power' },
	{ id: 'voltage', keys: ['voltage'], label: 'Voltage' },
	{ id: 'brightness', keys: ['brightness'], label: 'Brightness' },
	{
		id: 'temperature',
		keys: ['temperature', 'curr_temperature', 'current_temperature', 'average_temperature'],
		label: 'Temperature',
	},
	{ id: 'humidity', keys: ['humidity', 'current_humidity'], label: 'Humidity' },
	{ id: 'door', keys: ['door'], label: 'Door' },
	{ id: 'tilt', keys: ['tilt'], label: 'Tilt' },
	{ id: 'lux', keys: ['lux'], label: 'Lux' },
	{ id: 'position', keys: ['position'], label: 'Position' },
	{ id: 'has_power', keys: ['has_power'], label: 'Powered' },
	{ id: 'precipitation', keys: ['last_precipitation'], label: 'Precipitation' },
	{ id: 'battery', keys: ['battery'], label: 'Battery' },
];

const humanizeStatusKey = (key: string): string =>
	key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

export const columnForStatusKey = (key: string): DeviceTableStatusColumn => {
	const existing = DEVICE_TABLE_STATUS_COLUMNS.find((column) => column.keys.includes(key));
	if (existing) return existing;
	return { id: key, keys: [key], label: humanizeStatusKey(key) };
};

const isHiddenStatus = (status: { hideLabel?: boolean; advLabel?: boolean; name?: string }): boolean =>
	!!status?.hideLabel || !!status?.advLabel || /^(timer|schedule|last)$/.test(status?.name || '');

export const requestedDashStatusKeys = (
	device: Device | null | undefined,
	devicesConfig: Record<string, DeviceConfigForDashLabels | undefined> = devices_config as Record<
		string,
		DeviceConfigForDashLabels | undefined
	>,
): string[] => {
	if (!device) return [];
	return expandLabelConfig(resolveDashInfoLabelsConfig(devicesConfig, device));
};

/**
 * Union of dash-info-label preset keys across the visible device list.
 * Same source as DashDeviceCard — not AdvancedCard's customize-label dump.
 */
export const collectDeviceTableStatusColumns = (
	devices: Array<Device | null | undefined>,
	devicesConfig: Record<string, DeviceConfigForDashLabels | undefined> = devices_config as Record<
		string,
		DeviceConfigForDashLabels | undefined
	>,
): DeviceTableStatusColumn[] => {
	const seen = new Set<string>();
	const extras: DeviceTableStatusColumn[] = [];

	for (const device of devices) {
		for (const key of requestedDashStatusKeys(device, devicesConfig)) {
			const column = columnForStatusKey(key);
			if (seen.has(column.id)) continue;
			seen.add(column.id);
			if (!DEVICE_TABLE_STATUS_COLUMNS.some((item) => item.id === column.id)) {
				extras.push(column);
			}
		}
	}

	return [...DEVICE_TABLE_STATUS_COLUMNS.filter((column) => seen.has(column.id)), ...extras];
};

export const matchStatusForColumn = (
	statuses: any[] | undefined,
	column: DeviceTableStatusColumn,
): any | null => {
	if (!Array.isArray(statuses) || !column) return null;
	for (const key of column.keys) {
		const found = statuses.find((item) => statusMatchesKey(item, key) && !isHiddenStatus(item));
		if (found) return found;
	}
	return null;
};

export const formatDeviceTableStatusCell = (status: any): DeviceTableStatusCell | null => {
	if (!status) return null;

	const value =
		status.value === 0 || status.value
			? String(status.value)
			: status.value === '' || status.value == null
				? undefined
				: String(status.value);

	if (value == null && !status.icon) return null;

	return {
		value,
		icon: mapIconSolidWeightToLight(status.icon),
	};
};

export const resolveDeviceTableStatusCell = (
	statuses: any[] | undefined,
	column: DeviceTableStatusColumn,
): DeviceTableStatusCell | null => formatDeviceTableStatusCell(matchStatusForColumn(statuses, column));

export const formatDeviceCategoryLabel = (category: string | undefined): string => {
	if (!category) return '—';
	return category.replace(/_/g, ' ');
};

export const DEVICE_TABLE_NAME_COLUMN_ID = 'name';
export const DEVICE_TABLE_STATE_COLUMN_ID = 'state';
export const DEVICE_TABLE_ROOM_COLUMN_ID = 'room';
export const DEVICE_TABLE_APPLIANCE_COLUMN_ID = 'appliance';
export const DEVICE_TABLE_TYPE_COLUMN_ID = 'type';

export type DeviceTableColumnKind = 'name' | 'state' | 'room' | 'appliance' | 'type' | 'status';

export type DeviceTableColumn = {
	id: string;
	label: string;
	kind: DeviceTableColumnKind;
	status?: DeviceTableStatusColumn;
};

const IDENTITY_COLUMNS: DeviceTableColumn[] = [
	{ id: DEVICE_TABLE_STATE_COLUMN_ID, label: 'State', kind: 'state' },
	{ id: DEVICE_TABLE_ROOM_COLUMN_ID, label: 'Room', kind: 'room' },
	{ id: DEVICE_TABLE_APPLIANCE_COLUMN_ID, label: 'Appliance', kind: 'appliance' },
	{ id: DEVICE_TABLE_TYPE_COLUMN_ID, label: 'Type', kind: 'type' },
];

export const parseSavedDeviceTableColumns = (value: unknown): string[] | null => {
	if (!Array.isArray(value)) return null;
	const ids = value.filter((item): item is string => typeof item === 'string' && item.length > 0);
	return ids.length ? ids : null;
};

export const buildDefaultMovableColumnIds = (statusIds: string[]): string[] => [
	DEVICE_TABLE_STATE_COLUMN_ID,
	DEVICE_TABLE_ROOM_COLUMN_ID,
	DEVICE_TABLE_APPLIANCE_COLUMN_ID,
	...statusIds,
	DEVICE_TABLE_TYPE_COLUMN_ID,
];

/**
 * Replays a saved movable-column sequence onto the columns that currently
 * exist. Unknown ids are dropped; newly appeared status columns insert
 * immediately before Type so Type stays last unless the user already moved it.
 */
export const applyDeviceTableColumnPreference = (
	availableIds: string[],
	savedIds?: string[] | null,
): string[] => {
	if (!savedIds?.length) return [...availableIds];

	const available = new Set(availableIds);
	const ordered: string[] = [];
	for (const id of savedIds) {
		if (!available.has(id) || ordered.includes(id)) continue;
		ordered.push(id);
	}

	const missing = availableIds.filter((id) => !ordered.includes(id));
	if (!missing.length) return ordered;

	const typeIndex = ordered.indexOf(DEVICE_TABLE_TYPE_COLUMN_ID);
	if (typeIndex >= 0) {
		ordered.splice(typeIndex, 0, ...missing);
		return ordered;
	}

	return [...ordered, ...missing];
};

export const shiftDeviceTableColumn = (ids: string[], columnId: string, direction: -1 | 1): string[] => {
	const index = ids.indexOf(columnId);
	const next = index + direction;
	if (index < 0 || next < 0 || next >= ids.length) return [...ids];
	const copy = [...ids];
	const [moved] = copy.splice(index, 1);
	copy.splice(next, 0, moved);
	return copy;
};

export const movableDeviceTableColumnIds = (columns: DeviceTableColumn[]): string[] =>
	columns.filter((column) => column.id !== DEVICE_TABLE_NAME_COLUMN_ID).map((column) => column.id);

export const buildDeviceTableColumns = (
	devices: Array<Device | null | undefined>,
	savedOrder?: string[] | null,
	devicesConfig: Record<string, DeviceConfigForDashLabels | undefined> = devices_config as Record<
		string,
		DeviceConfigForDashLabels | undefined
	>,
): DeviceTableColumn[] => {
	const statusColumns = collectDeviceTableStatusColumns(devices, devicesConfig);
	const byId = new Map<string, DeviceTableColumn>();

	for (const column of IDENTITY_COLUMNS) {
		byId.set(column.id, column);
	}
	for (const status of statusColumns) {
		byId.set(status.id, {
			id: status.id,
			label: status.label,
			kind: 'status',
			status,
		});
	}

	const movableIds = applyDeviceTableColumnPreference(
		buildDefaultMovableColumnIds(statusColumns.map((column) => column.id)),
		savedOrder,
	);

	return [
		{ id: DEVICE_TABLE_NAME_COLUMN_ID, label: 'Name', kind: 'name' },
		...movableIds
			.map((id) => byId.get(id))
			.filter((column): column is DeviceTableColumn => Boolean(column)),
	];
};
